import type { ReportSummary } from '@quickreport/domain';
import { Button, Field, Modal, Select, Textarea } from '@quickreport/ui';
import { useState } from 'react';

export interface SignatoryOption {
  id: string;
  name: string;
  qualification: string;
  member_id: string | null;
}

export function SignDialog({
  partial,
  summary,
  signatories,
  defaultSignatory,
  onCancel,
  onConfirm,
  error,
}: {
  partial: boolean;
  summary: ReportSummary;
  signatories: SignatoryOption[];
  defaultSignatory: string | null;
  onCancel: () => void;
  /** `signatoryId` is null when the lab prints no signatures and has no signing doctor. */
  onConfirm: (signatoryId: string | null, criticalReviewed: boolean) => void;
  error: string | null;
}) {
  const [who, setWho] = useState(defaultSignatory ?? signatories[0]?.id ?? '');
  const [reviewed, setReviewed] = useState(false);
  const awaited = summary.testCount - summary.filledCount;
  return (
    <Modal
      title={partial ? 'Release a partial report' : 'Sign this report'}
      onClose={onCancel}
      label={partial ? 'Release partial report' : 'Sign report'}
    >
      {signatories.length === 0 ? null : (
        <Field label="Signing as">
          <Select value={who} onChange={(e) => setWho(e.target.value)}>
            {signatories.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.qualification}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {partial ? (
        <p className="text-[13px] text-slate">
          {awaited} result{awaited === 1 ? '' : 's'} not filled in will print as “Awaited”, with a
          PARTIAL mark on the page. You can sign the complete report later under the same QR code.
        </p>
      ) : (
        <p className="text-[13px] text-slate">
          Signing freezes the results as version 1. After that the report can only be corrected by
          amending it, which is recorded.
        </p>
      )}
      {summary.flagged.length ? (
        <div className="rounded-md bg-canvas p-3 text-[13px]">
          <div className="mb-1 font-medium">
            {summary.flagged.length} value{summary.flagged.length === 1 ? '' : 's'} outside the
            range
          </div>
          <ul className="grid gap-0.5">
            {summary.flagged.slice(0, 8).map((f) => (
              <li key={`${f.label}-${f.value}`} className="flex justify-between gap-3">
                <span>{f.label}</span>
                <span className="font-medium text-flag">
                  {f.value} {f.flag}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {summary.critical > 0 ? (
        <label className="flex gap-2 rounded-md border border-flag bg-red-50 p-3 text-[13px] text-flag">
          <input
            type="checkbox"
            checked={reviewed}
            onChange={(e) => setReviewed(e.target.checked)}
            className="mt-0.5 accent-flag"
          />
          <span>
            <b>
              {summary.critical} critical value{summary.critical === 1 ? '' : 's'}.
            </b>{' '}
            I have reviewed {summary.critical === 1 ? 'it' : 'them'} and, where needed, informed the
            doctor.
          </span>
        </label>
      ) : null}
      {error ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          tone="primary"
          disabled={(signatories.length > 0 && !who) || (summary.critical > 0 && !reviewed)}
          onClick={() => onConfirm(who || null, reviewed)}
        >
          {partial ? 'Release partial report' : 'Sign and PDF'}
        </Button>
      </div>
    </Modal>
  );
}

export function ReasonDialog({
  title,
  prompt,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
  error,
}: {
  title: string;
  prompt: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  error: string | null;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal title={title} onClose={onCancel} label={title}>
      <Field label={prompt}>
        <Textarea autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {error ? (
        <p role="alert" className="text-[13px] text-flag">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          tone={danger ? 'danger' : 'primary'}
          className={danger ? 'border border-flag' : ''}
          disabled={reason.trim().length < 3}
          onClick={() => onConfirm(reason)}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Shown when the lab's reports print a doctor's signature but no signing doctor has been added.
 * The Sign button is never greyed out for this: a person who clicks it is told what to do.
 */
export function SignatureNeededDialog({
  canChange,
  onAdd,
  onTurnOff,
  onCancel,
}: {
  /** Only an owner or admin can change the lab's settings. */
  canChange: boolean;
  onAdd: () => void;
  onTurnOff: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal title="A signing doctor is needed" onClose={onCancel} label="Signing doctor needed">
      <p>
        Your reports are set to print a doctor’s signature, but no signing doctor has been added
        yet.
      </p>
      {canChange ? (
        <div className="grid gap-2">
          <Button tone="primary" className="h-11 justify-center" onClick={onAdd}>
            Add a signing doctor
          </Button>
          <Button className="h-11 justify-center" onClick={onTurnOff}>
            Do not print signatures, and sign this report
          </Button>
        </div>
      ) : (
        <p className="rounded-md bg-canvas px-3 py-2 text-[13px] text-slate">
          Ask the owner or an admin of your lab to add a signing doctor in Report Settings, or to
          switch signatures off.
        </p>
      )}
      <p className="rounded-md bg-green-50 px-3 py-2 text-[13px] text-ok">
        Don’t worry: everything you have entered on this report is already saved. You can come back
        and sign it at any time.
      </p>
      <div className="flex justify-end">
        <Button tone="ghost" onClick={onCancel}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
